import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, PenLine, ImageIcon, CheckCircle2, Sparkles, X, Wallet, TrendingUp, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthorEarnings({ token }) {
  const [earnings, setEarnings] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [showClaim, setShowClaim] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/editorial-submissions/${token}/earnings`)
      .then((r) => setEarnings(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!earnings || !earnings.promo_active) return null;
  const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const claim = async () => {
    if (password.length < 8) return toast.error('Password must be at least 8 characters.');
    if (password !== confirm) return toast.error("Passwords don't match.");
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/editorial-submissions/${token}/claim-account`, { password });
      localStorage.setItem('eventism_token', r.data.token);
      toast.success('Account created — your earnings moved with you!');
      setShowClaim(false);
      load();
      setTimeout(() => { window.location.assign('/dashboard/street-team'); }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/5 via-transparent to-transparent mt-6" data-testid="author-earnings">
      <CardContent className="p-6 space-y-4">
        <h2 className="font-bold flex items-center gap-2">
          <Wallet className="h-4 w-4 text-fuchsia-300" /> Your guide is earning
        </h2>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Every reader who books tickets to a promoter-enabled event within 30 days of reading your guide earns you a commission — automatically, forever.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-lg font-bold text-emerald-300" data-testid="earn-lifetime">{fmt(earnings.lifetime_cents)}</p>
            <p className="text-[10px] text-muted-foreground">Lifetime earned</p>
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-lg font-bold text-amber-300">{fmt(earnings.pending_cents)}</p>
            <p className="text-[10px] text-muted-foreground">Pending release</p>
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-lg font-bold text-fuchsia-300">{fmt(earnings.available_cents)}</p>
            <p className="text-[10px] text-muted-foreground">Ready to cash out</p>
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-lg font-bold">{earnings.sales_count}<span className="text-xs text-muted-foreground"> / {earnings.clicks}</span></p>
            <p className="text-[10px] text-muted-foreground">Sales / reads</p>
          </div>
        </div>
        {(earnings.transactions || []).length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {earnings.transactions.map((t, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] border-b border-border/40 py-1.5">
                <span className="truncate">{t.event_name || 'Event'} <span className="text-muted-foreground">· {new Date(t.created_at).toLocaleDateString()}</span></span>
                <span className={t.status === 'pending' ? 'text-amber-300' : 'text-emerald-300'}>+{fmt(t.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
        {earnings.claimed ? (
          <a href="/dashboard/street-team" className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:underline" data-testid="earn-goto-dashboard">
            <TrendingUp className="h-3.5 w-3.5" /> Open your promoter dashboard to cash out →
          </a>
        ) : showClaim ? (
          <div className="space-y-2 rounded-lg border border-fuchsia-500/30 p-3" data-testid="claim-form">
            <p className="text-xs text-muted-foreground">Create your Eventism account (uses your invite email) — your balance moves with you and unlocks Stripe cash-outs.</p>
            <Input type="password" placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-password" />
            <Input type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="claim-confirm" />
            <Button size="sm" onClick={claim} disabled={claiming} className="bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white border-0" data-testid="claim-submit">
              {claiming ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />} Create account & claim earnings
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="border-fuchsia-500/40 text-fuchsia-300" onClick={() => setShowClaim(true)} data-testid="claim-open">
            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Claim your earnings — create your account
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function EditorialSubmissionPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copy, setCopy] = useState('');
  const [bio, setBio] = useState('');
  const [instagram, setInstagram] = useState('');
  const [website, setWebsite] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState('write'); // write | processing | published
  const [guideUrl, setGuideUrl] = useState(null);
  const fileRef = useRef(null);
  const avatarRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/editorial-submissions/${token}`)
      .then((r) => {
        setInfo(r.data);
        setInstagram(r.data.instagram || '');
        if (r.data.status === 'published' && r.data.guide_url) {
          setPhase('published');
          setGuideUrl(r.data.guide_url);
        } else if (r.data.status === 'processing') {
          setPhase('processing');
        }
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const pollStatus = useCallback(() => {
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/editorial-submissions/${token}/status`);
        if (r.data.status === 'published') {
          clearInterval(id);
          setGuideUrl(r.data.guide_url);
          setPhase('published');
        } else if (r.data.status === 'error') {
          clearInterval(id);
          toast.error('Something went wrong on our side — the Eventism team has been notified.');
          setPhase('write');
        }
      } catch (e) { /* keep polling */ }
    }, 5000);
    return id;
  }, [token]);

  useEffect(() => {
    if (phase !== 'processing') return undefined;
    const id = pollStatus();
    return () => clearInterval(id);
  }, [phase, pollStatus]);

  const uploadFiles = async (files, isAvatar = false) => {
    setUploading(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10 MB`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await axios.post(`${API}/editorial-submissions/${token}/upload-photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        });
        if (isAvatar) setAvatarUrl(r.data.url);
        else setPhotos((p) => [...p, r.data.url].slice(0, 8));
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (copy.trim().length < 200) return toast.error('Give us a bit more — at least a short intro plus your picks (200+ characters).');
    try {
      setPhase('processing');
      await axios.post(`${API}/editorial-submissions/${token}`, {
        copy, photos, bio, avatar_url: avatarUrl || undefined,
        instagram: instagram || undefined, website: website || undefined,
      });
    } catch (e) {
      setPhase('write');
      toast.error(e?.response?.data?.detail || 'Submit failed — try again.');
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md border-border"><CardContent className="p-8 text-center">
          <p className="font-bold text-lg mb-2">This link isn&apos;t valid</p>
          <p className="text-sm text-muted-foreground">Check the link in your invite email, or reply to it and we&apos;ll send a fresh one.</p>
        </CardContent></Card>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fuchsia-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.25em] text-fuchsia-300 font-bold mb-2">EVENTISM EDITORIAL</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="contrib-title">{info.proposed_title}</h1>
          <p className="text-sm text-muted-foreground mt-2">by <span className="text-foreground font-semibold">{info.name}</span> · {String(info.city).charAt(0).toUpperCase() + String(info.city).slice(1)}</p>
        </div>

        {phase === 'published' && (
          <>
            <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="contrib-published">
              <CardContent className="p-8 text-center space-y-4">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                <p className="font-bold text-xl">You&apos;re live! 🎉</p>
                <p className="text-sm text-muted-foreground">Your guide is published with your byline, photo, and socials.</p>
                <a href={guideUrl} className="inline-block rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white font-bold px-8 py-3 text-sm" data-testid="contrib-live-link">
                  See your guide →
                </a>
              </CardContent>
            </Card>
            <AuthorEarnings token={token} />
          </>
        )}

        {phase === 'processing' && (
          <Card className="border-amber-500/40 bg-amber-500/5" data-testid="contrib-processing">
            <CardContent className="p-8 text-center space-y-4">
              <Sparkles className="h-10 w-10 text-amber-300 mx-auto animate-pulse" />
              <p className="font-bold text-xl">Our editor is on it…</p>
              <p className="text-sm text-muted-foreground">The Eventism editorial AI is assembling your guide — matching venues, pulling photos, formatting. Takes about a minute. Don&apos;t close this page.</p>
              <Loader2 className="h-5 w-5 animate-spin text-amber-300 mx-auto" />
            </CardContent>
          </Card>
        )}

        {phase === 'write' && (
          <Card className="border-border/60" data-testid="contrib-form">
            <CardContent className="p-6 space-y-5">
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 text-xs text-muted-foreground leading-relaxed">
                <PenLine className="h-3.5 w-3.5 text-fuchsia-300 inline mr-1.5" />
                Write it your way — intro, your picks (venue names + why), any order, any format. Our editor handles photos, venue details, formatting, and publishing. Your voice stays untouched.
              </div>
              <div className="space-y-1.5">
                <Label>Your guide copy</Label>
                <Textarea rows={14} value={copy} onChange={(e) => setCopy(e.target.value)}
                  placeholder={`Denver runs deep for this scene — here's where I'd actually send you.\n\nThe Church Nightclub\nThe main room on a Friday is unmatched...\n\nBeta Event Center\n...`}
                  className="text-sm" data-testid="contrib-copy" />
                <p className="text-[10px] text-muted-foreground text-right">{copy.length} characters</p>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center justify-between">
                  <span>Your photos (optional, up to 8)</span>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center gap-1 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-1 text-[11px] font-semibold text-fuchsia-200"
                    data-testid="contrib-photo-upload">
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />} Upload
                  </button>
                </Label>
                <input type="file" ref={fileRef} accept="image/*" multiple className="hidden"
                  onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {photos.map((u) => (
                      <div key={u} className="relative">
                        <img src={u} alt="" className="h-16 w-16 rounded-md object-cover" />
                        <button onClick={() => setPhotos((p) => p.filter((x) => x !== u))}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Instagram</Label>
                  <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/you" data-testid="contrib-instagram" />
                </div>
                <div className="space-y-1.5">
                  <Label>Website (optional)</Label>
                  <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." data-testid="contrib-website" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center justify-between">
                  <span>Short bio (shows on your byline)</span>
                  <button type="button" onClick={() => avatarRef.current?.click()} disabled={uploading}
                    className="text-[11px] text-fuchsia-300 underline" data-testid="contrib-avatar-upload">
                    {avatarUrl ? 'Change headshot' : '+ Add headshot'}
                  </button>
                </Label>
                <input type="file" ref={avatarRef} accept="image/*" className="hidden"
                  onChange={(e) => { uploadFiles(Array.from(e.target.files || []).slice(0, 1), true); e.target.value = ''; }} />
                {avatarUrl && <img src={avatarUrl} alt="headshot" className="h-14 w-14 rounded-full object-cover" />}
                <Textarea rows={2} value={bio} onChange={(e) => setBio(e.target.value)}
                  placeholder="Denver's go-to Hip Hop & R&B DJ. 10+ years behind the decks…" data-testid="contrib-bio" />
              </div>
              <Button onClick={submit} disabled={uploading}
                className="w-full bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 font-bold py-6 text-base"
                data-testid="contrib-submit">
                Submit — publish my guide
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">By submitting you confirm the writing is yours and grant Eventism the right to publish it with your byline.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
